import { ref, set, push, remove, onValue, get } from 'firebase/database';
import { useState, useEffect } from 'react';
import { db } from '../firebase';

// ── 固定の規定フィールド（常に先頭に表示・必須） ───────────────────────────
export const FIXED_FIELDS = [
  { id: '_vrchat_name',    key: 'vrchat_name',    label: 'VRChat表示名',          placeholder: 'あなたのVRChatでの表示名', required: true,  type: 'standard' },
  { id: '_vrchat_profile', key: 'vrchat_profile', label: 'VRChatプロフィールURL', placeholder: 'https://vrchat.com/home/user/usr_...', required: true,  type: 'standard' },
];

// ── 追加できる規定フィールド ─────────────────────────────────────────────
export const OPTIONAL_STANDARD_FIELDS = [
  { id: '_x_url',   key: 'x_url',   label: 'X (Twitter) URL', placeholder: 'https://x.com/yourname', inputType: 'text' },
  { id: '_discord', key: 'discord', label: 'Discord ID',       placeholder: 'username#0000',          inputType: 'text' },
];

// ── owner/staff 用: イベントのフォーム定義・応募を管理 ───────────────────
export const useApplicationForm = (eventId) => {
  const [formDef, setFormDef]         = useState({ fields: [] });
  const [applications, setApplications] = useState({});
  const [formToken, setFormToken]     = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (!eventId) return;

    const unsubs = [];

    // フォーム定義
    const formRef = ref(db, `events/${eventId}/form_definition`);
    unsubs.push(onValue(formRef, (snap) => {
      setFormDef(snap.val() || { fields: [] });
    }));

    // アクティブトークン
    const tokenRef = ref(db, `events/${eventId}/form_token`);
    unsubs.push(onValue(tokenRef, (snap) => {
      setFormToken(snap.val() || null);
    }));

    // 応募一覧
    const appRef = ref(db, `events/${eventId}/applications`);
    unsubs.push(onValue(appRef, (snap) => {
      setApplications(snap.val() || {});
      setLoading(false);
    }));

    return () => unsubs.forEach(u => u());
  }, [eventId]);

  /** フォーム定義を保存（テンプレートとして上書き） */
  const saveFormDef = (fields, title = '', description = '') =>
    set(ref(db, `events/${eventId}/form_definition`), { fields, title, description });

  /** 応募受付開始: トークン生成 → formTokens に登録 */
  const generateToken = async () => {
    const token = crypto.randomUUID();
    await Promise.all([
      set(ref(db, `events/${eventId}/form_token`), token),
      set(ref(db, `formTokens/${token}`), { event_id: eventId }),
    ]);
    return token;
  };

  /** 応募受付終了: トークン削除 */
  const deleteToken = async (token) => {
    if (!token) return;
    await Promise.all([
      remove(ref(db, `events/${eventId}/form_token`)),
      remove(ref(db, `formTokens/${token}`)),
    ]);
  };

  /** 応募データをリセット（フェーズを準備中に戻す時） */
  const clearApplications = () =>
    remove(ref(db, `events/${eventId}/applications`));

  return {
    formDef,
    applications,
    formToken,
    loading,
    saveFormDef,
    generateToken,
    deleteToken,
    clearApplications,
  };
};

// ── 公開フォームページ用: トークンからイベント情報と定義を取得 ──────────
export const usePublicApplicationForm = (token) => {
  const [state, setState] = useState({ loading: true, invalid: false, eventId: null, formDef: null, event: null });

  useEffect(() => {
    if (!token) { setState(s => ({ ...s, loading: false, invalid: true })); return; }

    get(ref(db, `formTokens/${token}`)).then((snap) => {
      if (!snap.exists()) {
        setState({ loading: false, invalid: true, eventId: null, formDef: null, event: null });
        return;
      }
      const { event_id } = snap.val();

      // 認証不要なパスを個別に取得（events/$eventId 全体は auth 必須のため）
      Promise.all([
        get(ref(db, `events/${event_id}/event_name`)),
        get(ref(db, `events/${event_id}/status`)),
        get(ref(db, `events/${event_id}/form_definition`)),
      ]).then(([nameSnap, statusSnap, formSnap]) => {
        const partial = {
          event_name:      nameSnap.val(),
          status:          statusSnap.val(),
          form_definition: formSnap.val(),
        };
        if (partial.status !== 'matching') {
          setState({ loading: false, invalid: true, eventId: event_id, formDef: null, event: partial });
          return;
        }
        setState({
          loading: false, invalid: false,
          eventId: event_id,
          formDef: partial.form_definition || { fields: [] },
          event: partial,
        });
      }).catch(() => setState({ loading: false, invalid: true, eventId: null, formDef: null, event: null }));
    }).catch(() => setState({ loading: false, invalid: true, eventId: null, formDef: null, event: null }));

  }, [token]);

  /** 応募を送信してゲスト登録 */
  const submitApplication = async ({ vrchat_name, vrchat_profile, x_url, discord, answers, user_id, image_url }) => {
    const { eventId } = state;
    if (!eventId) throw new Error('invalid');

    // 重複チェック
    const guestsSnap = await get(ref(db, `events/${eventId}/guests`));
    const guests = guestsSnap.val() || {};
    const duplicate = Object.values(guests).find(
      g => g.vrchat_name?.trim().toLowerCase() === vrchat_name.trim().toLowerCase()
    );
    if (duplicate) throw new Error('duplicate');

    const now = Date.now();

    // ゲスト登録
    const guestRef = push(ref(db, `events/${eventId}/guests`));
    await set(guestRef, {
      vrchat_name:    vrchat_name.trim(),
      vrchat_profile: vrchat_profile?.trim() || null,
      x_url:          x_url?.trim()    || null,
      discord:        discord?.trim()   || null,
      image_url:      image_url         || null,
      user_id:        user_id           || null,
      status:         'waiting',
      priority:       0,
      draft_picks:    [],
      created_at:     now,
    });

    // 応募記録
    const appRef = push(ref(db, `events/${eventId}/applications`));
    await set(appRef, {
      guest_id:       guestRef.key,
      vrchat_name:    vrchat_name.trim(),
      vrchat_profile: vrchat_profile?.trim() || null,
      x_url:          x_url?.trim()    || null,
      discord:        discord?.trim()   || null,
      user_id:        user_id           || null,
      answers:        answers || {},
      submitted_at:   now,
    });
  };

  return { ...state, submitApplication };
};
